import { Fragment, useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { recentEntries, RECENT_LIMIT } from '@/lib/utils';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Receipt as ReceiptIcon, Check, CaretUpDown, User, PencilSimple, Trash, FileText, X, ArrowsLeftRight } from '@phosphor-icons/react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useCan } from '@/lib/permissions';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

/** Customer combobox — search by name or phone. */
function CustomerCombo({ customers, value, onChange, testId, disabled }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = (customers || []).filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.phone || '').includes(search)
  );
  const selected = customers.find(c => c.id === value);
  return (
    <Popover open={open && !disabled} onOpenChange={(v) => !disabled && setOpen(v)}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" disabled={disabled}
          className="w-full justify-between rounded-lg h-9 text-sm font-normal border-[#E4E4E7] hover:border-[#A1A1AA] hover:bg-white mt-1 disabled:opacity-70"
          data-testid={testId}>
          <span className={selected ? 'text-[#0A0A0A]' : 'text-[#A1A1AA]'}>
            {selected ? `${selected.name} ${selected.phone ? `· ${selected.phone}` : ''}` : 'Select customer...'}
          </span>
          <CaretUpDown size={12} className="ml-1 shrink-0 text-[#A1A1AA]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-lg" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search by name or phone..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty className="py-2 text-center text-xs text-[#A1A1AA]">No match</CommandEmpty>
            <CommandGroup>
              {filtered.slice(0, 50).map(c => (
                <CommandItem key={c.id} value={c.id} onSelect={() => { onChange(c.id); setOpen(false); setSearch(''); }} className="cursor-pointer text-sm">
                  <span className="flex-1 truncate">
                    {c.name}
                    {c.phone && <span className="text-[10px] text-[#A1A1AA] ml-2">{c.phone}</span>}
                  </span>
                  {(c.outstanding_balance || 0) > 0 && (
                    <span className="text-[10px] font-mono text-[#EF4444] mr-2">{fmt(c.outstanding_balance)} due</span>
                  )}
                  {c.id === value && <Check size={14} className="text-[#3B82F6]" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Receipt entry page — single-leg receipt that credits the customer's
 * advance balance + receiving account. Allocation against open invoices is
 * done separately via the invoice's pay flow.
 *
 * The same form serves both Create and Edit. When `editingVoucher` is set,
 * the heading reads "Edit RCP-XXXXX" and Save calls PUT instead of POST.
 */
export default function ReceiptPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerParam = searchParams.get('customer');
  const returnToLedger = searchParams.get('return_to_ledger') === '1';
  const ledgerReturnTarget = returnToLedger && customerParam
    ? `/customers?openLedger=${encodeURIComponent(customerParam)}`
    : '/finance';
  const goBack = () => navigate(ledgerReturnTarget);
  const canEdit = useCan('sales', 'edit');
  const canDelete = useCan('sales', 'delete');

  const [customers, setCustomers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [recentReceipts, setRecentReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Default view = today's receipts only. The date picker in the
  // Recent Receipts header lets the user jump to any historical day.
  const [filterDate, setFilterDate] = useState('');

  const initialForm = () => ({
    customer_id: customerParam || '',
    amount: '',
    account: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [form, setForm] = useState(initialForm);

  // null when creating; otherwise the receipt row being edited.
  const [editingVoucher, setEditingVoucher] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Bulk-delete state for the Recent Receipts register.
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(false);
  // Pending invoices for the selected customer + per-bill allocation amounts.
  // Allocations let the user split the receipt across one or more open bills;
  // anything left over stays as customer advance.
  const [pendingBills, setPendingBills] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [loadingBills, setLoadingBills] = useState(false);

  const fetchData = useCallback(() => {
    Promise.all([
      api.get('/customers'),
      api.get('/finance/accounts'),
      api.get('/finance/receipts'),
    ]).then(([cu, ac, rc]) => {
      setCustomers(cu.data || []);
      // Credit cards are only usable when their balance is zero or negative
      // (i.e., not over-paid). Cash / bank / wallet / creditor accounts are
      // always available regardless of balance.
      setAccounts((ac.data || []).filter(a => {
        if (['cash', 'bank', 'wallet', 'creditor'].includes(a.type)) return true;
        if (a.type === 'credit_card') return (a.balance || 0) <= 0;
        return false;
      }));
      setRecentReceipts(rc.data || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { setLoading(true); fetchData(); }, [fetchData]);

  // When the customer changes, pull their open invoices so the user can
  // optionally split the receipt across them. Resets any prior allocations.
  useEffect(() => {
    if (!form.customer_id) { setPendingBills([]); setAllocations({}); return; }
    setLoadingBills(true);
    api.get(`/finance/customers/${form.customer_id}/open-invoices`)
      .then(r => setPendingBills(r.data || []))
      .catch(() => setPendingBills([]))
      .finally(() => setLoadingBills(false));
    setAllocations({});
  }, [form.customer_id]);

  // FIFO auto-fill: spread the entered amount across pending bills oldest-first.
  const autoAllocate = () => {
    const amt = parseFloat(form.amount) || 0;
    if (amt <= 0 || pendingBills.length === 0) return;
    let remaining = amt;
    const next = {};
    for (const inv of pendingBills) {
      if (remaining <= 0) break;
      const due = Math.max(0, inv.balance_due || 0);
      const take = Math.min(due, remaining);
      if (take > 0) {
        next[inv.id] = take;
        remaining -= take;
      }
    }
    setAllocations(next);
  };

  const totalAllocated = Object.values(allocations).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const advanceLeftover = Math.max(0, (parseFloat(form.amount) || 0) - totalAllocated);

  const startEdit = (r) => {
    setEditingVoucher(r);
    setForm({
      customer_id: r.customer_id || '',
      amount: String(r.amount ?? ''),
      account: r.account || '',
      description: r.description || '',
      date: (r.date || '').slice(0, 10) || new Date().toISOString().split('T')[0],
    });
    // Scroll to top so the user sees the form
    document.querySelector('[data-testid="receipt-form-card"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const cancelEdit = () => {
    setEditingVoucher(null);
    setForm(initialForm());
  };

  const handleSubmit = async () => {
    if (!form.customer_id || !form.amount || !form.account) {
      toast.error('Pick a customer, account and amount');
      return;
    }
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error('Amount must be greater than 0'); return; }

    // Validate that no allocation row exceeds its bill's open balance.
    for (const inv of pendingBills) {
      const al = parseFloat(allocations[inv.id]) || 0;
      if (al < 0) { toast.error(`Allocation for ${inv.invoice_number} cannot be negative`); return; }
      if (al > (inv.balance_due || 0) + 0.01) {
        toast.error(`Allocation for ${inv.invoice_number} exceeds its open balance ${fmt(inv.balance_due)}`);
        return;
      }
    }
    if (totalAllocated > amt + 0.01) {
      toast.error(`Total allocated (${fmt(totalAllocated)}) exceeds receipt amount (${fmt(amt)})`);
      return;
    }

    setSubmitting(true);
    try {
      let voucherNo = '';
      if (editingVoucher) {
        await api.put(`/finance/receipts/${editingVoucher.voucher_number}`, {
          customer_id: form.customer_id,
          account: form.account,
          amount: amt,
          date: form.date,
          description: form.description,
        });
        voucherNo = editingVoucher.voucher_number;
      } else {
        const res = await api.post('/finance/receipt', {
          customer_id: form.customer_id,
          amount: amt,
          payment_mode: form.account,
          date: form.date,
          description: form.description,
        });
        voucherNo = res.data?.voucher_number || '';
      }
      // Apply each non-zero allocation against its invoice. We call
      // /apply-advance which draws from the customer's advance pool — the
      // pool was just topped up by `amt` on the line above, so it's safe.
      const appliedSummary = [];
      for (const inv of pendingBills) {
        const al = parseFloat(allocations[inv.id]) || 0;
        if (al <= 0) continue;
        try {
          await api.post(`/invoices/${inv.id}/apply-advance`, { amount: al });
          appliedSummary.push(`${inv.invoice_number}: ${fmt(al)}`);
        } catch (e) {
          toast.error(`Could not apply ${fmt(al)} to ${inv.invoice_number}: ${e.response?.data?.detail || 'failed'}`);
        }
      }
      const summary = appliedSummary.length
        ? `Receipt ${voucherNo} saved, applied to ${appliedSummary.length} bill(s)`
        : `Receipt ${voucherNo} ${editingVoucher ? 'updated' : 'recorded'}`;
      toast.success(summary);
      if (!editingVoucher && returnToLedger && customerParam) { goBack(); return; }
      setEditingVoucher(null);
      setForm(initialForm());
      setAllocations({});
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save receipt');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReceipt = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/finance/receipts/${deleteTarget.voucher_number}`);
      toast.success(`Receipt ${deleteTarget.voucher_number} deleted`);
      setDeleteTarget(null);
      // If we were editing this voucher, cancel the edit.
      if (editingVoucher && editingVoucher.voucher_number === deleteTarget.voucher_number) cancelEdit();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete receipt');
    }
  };

  const performBulkDelete = async () => {
    if (bulkSelected.size === 0) return;
    setBulkDeleting(true);
    let ok = 0, failed = 0;
    for (const vch of bulkSelected) {
      try {
        await api.delete(`/finance/receipts/${vch}`);
        ok++;
      } catch {
        failed++;
      }
    }
    setBulkDeleting(false);
    setConfirmBulk(false);
    if (editingVoucher && bulkSelected.has(editingVoucher.voucher_number)) cancelEdit();
    setBulkSelected(new Set());
    if (ok > 0) toast.success(`Deleted ${ok} receipt${ok === 1 ? '' : 's'}${failed ? ` (${failed} failed)` : ''}`);
    if (ok === 0 && failed > 0) toast.error(`Failed to delete ${failed} receipt${failed === 1 ? '' : 's'}`);
    fetchData();
  };

  if (loading) return <div className="p-8 text-sm text-[#52525B]">Loading...</div>;

  // Edit-mode for existing vouchers that originated from invoice receipts (kind=invoice)
  // is restricted to date + description because their amount is bound to an invoice.
  const editLocked = editingVoucher && editingVoucher.kind === 'invoice';

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="receipt-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <button onClick={goBack} className="inline-flex items-center gap-1 text-xs text-[#52525B] hover:text-[#0A0A0A]" data-testid="back-to-balance-sheet">
          <ArrowLeft size={12} /> {returnToLedger ? 'Back to Customer Ledger' : 'Back to Balance Sheet'}
        </button>
      </div>

      {canEdit && (
      <Card className="rounded-xl border-[#E4E4E7] shadow-sm max-w-3xl" data-testid="receipt-form-card">
        <CardContent className="p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              {editingVoucher ? (
                <h2 className="text-base font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }} data-testid="receipt-form-heading">
                  Edit <span className="text-[#3B82F6]">{editingVoucher.voucher_number}</span>
                </h2>
              ) : (
                <h2 className="text-base font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }} data-testid="receipt-form-heading">
                  New Receipt
                </h2>
              )}
              <p className="text-xs text-[#52525B] mt-0.5">
                {editingVoucher
                  ? (editLocked ? 'Invoice-tied receipt — you can change the date, receiving account (e.g. Cash → Bank) and description.' : 'Update the receipt details below.')
                  : 'Records the amount as a customer advance. Apply against invoices later from the invoice screen.'}
              </p>
            </div>
            {editingVoucher && (
              <Button variant="ghost" size="sm" onClick={cancelEdit} className="rounded-lg text-[#52525B]" data-testid="cancel-edit-receipt">
                <X size={14} className="mr-1" /> Cancel
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Date *</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-lg mt-1" data-testid="receipt-date-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Receiving Account *</Label>
              <Select value={form.account} onValueChange={v => setForm({ ...form, account: v })}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="receipt-account-select"><SelectValue placeholder="Select account..." /></SelectTrigger>
                <SelectContent>
                  {accounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      <span className="flex items-center justify-between gap-3 w-full">
                        <span>{a.name} <span className="text-[10px] text-[#A1A1AA]">({a.type})</span></span>
                        <span className={`text-xs font-mono ${(a.balance || 0) > 0 ? 'text-[#10B981]' : 'text-[#A1A1AA]'}`}>{fmt(a.balance || 0)}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Customer *</Label>
            <CustomerCombo customers={customers} value={form.customer_id} onChange={v => setForm({ ...form, customer_id: v })} testId="receipt-customer-select" disabled={editLocked} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Amount Received *</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#A1A1AA]">&#8377;</span>
                <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0" className="rounded-lg pl-7 font-mono disabled:opacity-70" data-testid="receipt-amount-input" disabled={editLocked} />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional note" className="rounded-lg mt-1" data-testid="receipt-description-input" />
            </div>
          </div>

          {/* Pending bill-wise advance adjustment — when the customer has open
              invoices, let the user split this receipt across them. Anything
              not allocated stays as customer advance balance. */}
          {form.customer_id && !editLocked && (
            <div className="rounded-lg border border-[#E4E4E7] p-4 bg-[#FAFAFA]" data-testid="receipt-allocations-card">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#52525B]">Pending Bills</p>
                  <p className="text-[11px] text-[#A1A1AA] mt-0.5">Split this receipt across one or more open invoices. Remainder stays as advance.</p>
                </div>
                {pendingBills.length > 0 && (
                  <Button type="button" variant="outline" size="sm" onClick={autoAllocate} className="rounded-lg h-7 text-[11px] border-[#3B82F6] text-[#3B82F6]" data-testid="receipt-auto-allocate-btn">
                    Auto Fill (FIFO)
                  </Button>
                )}
              </div>

              {loadingBills ? (
                <p className="text-xs text-[#A1A1AA] italic">Loading pending bills...</p>
              ) : pendingBills.length === 0 ? (
                <p className="text-xs text-[#A1A1AA] italic" data-testid="receipt-no-pending-bills">No pending bills for this customer. The receipt will be parked entirely as an advance.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#E4E4E7]">
                        <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold w-32">Bill #</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Date</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Total</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Open Balance</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right w-40">Apply</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingBills.map(inv => (
                        <TableRow key={inv.id} className="border-[#E4E4E7]" data-testid={`receipt-allocate-row-${inv.invoice_number}`}>
                          <TableCell className="text-xs font-mono text-[#3B82F6]">{inv.invoice_number}</TableCell>
                          <TableCell className="text-xs text-[#52525B]">{inv.created_at ? new Date(inv.created_at).toLocaleDateString('en-IN') : '-'}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{fmt(inv.total)}</TableCell>
                          <TableCell className="text-xs text-right font-mono text-[#EF4444]">{fmt(inv.balance_due)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Input
                                type="number"
                                min={0}
                                step="0.01"
                                value={allocations[inv.id] ?? ''}
                                onChange={e => setAllocations({ ...allocations, [inv.id]: e.target.value })}
                                placeholder="0"
                                className="h-7 w-28 rounded-md text-right font-mono text-xs"
                                data-testid={`receipt-allocate-amount-${inv.invoice_number}`}
                              />
                              <button
                                type="button"
                                onClick={() => setAllocations({ ...allocations, [inv.id]: inv.balance_due })}
                                className="text-[10px] text-[#3B82F6] hover:text-[#2563EB] hover:underline whitespace-nowrap"
                                data-testid={`receipt-allocate-full-${inv.invoice_number}`}
                              >
                                Full
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {(parseFloat(form.amount) || 0) > 0 && (
                <div className="mt-3 flex items-center justify-between text-xs flex-wrap gap-2">
                  <p className="text-[#52525B]">
                    Allocated: <span className="font-mono font-semibold">{fmt(totalAllocated)}</span>
                    <span className="mx-2 text-[#A1A1AA]">·</span>
                    Will park as advance: <span className="font-mono font-semibold text-[#3B82F6]" data-testid="receipt-advance-leftover">{fmt(advanceLeftover)}</span>
                  </p>
                  {totalAllocated > (parseFloat(form.amount) || 0) + 0.01 && (
                    <p className="text-[#EF4444] font-semibold">Allocation exceeds receipt amount.</p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            {(returnToLedger || editingVoucher) && (
              <Button variant="outline" onClick={editingVoucher ? cancelEdit : goBack} className="rounded-xl border-[#E4E4E7]" data-testid="receipt-cancel-btn">Cancel</Button>
            )}
            {!editingVoucher && (
              <Button variant="outline" onClick={() => setForm(initialForm())} className="rounded-xl border-[#E4E4E7]" data-testid="receipt-reset-btn">Reset</Button>
            )}
            <Button onClick={handleSubmit} disabled={submitting} className="text-white rounded-xl bg-[#10B981] hover:bg-[#059669] hover:text-white" data-testid="save-receipt-btn">
              <ReceiptIcon size={14} weight="bold" className="mr-1" />
              {submitting ? 'Saving...' : (editingVoucher ? 'Save Changes' : 'Save Receipt')}
            </Button>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Recent receipts — every RCP voucher with edit + delete actions */}
      <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid="recent-receipts-card">
        <CardContent className="p-5 sm:p-6">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Recent Receipts</h2>
              <p className="text-xs text-[#52525B]" data-testid="receipt-recent-caption">{filterDate ? `Showing receipts dated ${new Date(filterDate).toLocaleDateString('en-IN')}` : `Showing last ${RECENT_LIMIT} receipts`}</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Date picker — default is today. Change it to view receipts
                  from any prior day; clears bulk selection so the user
                  doesn't accidentally delete rows that just scrolled off. */}
              <Input
                type="date"
                value={filterDate}
                onChange={(e) => { setFilterDate(e.target.value); setBulkSelected(new Set()); }}
                className="h-8 rounded-lg text-xs w-36"
                data-testid="receipt-recent-date-filter"
              />
<Button variant="ghost" size="sm" className={`h-8 px-2 text-xs ${!filterDate ? 'bg-[#EFF6FF] text-[#1D4ED8] font-semibold' : 'text-[#52525B]'}`}
                onClick={() => { setFilterDate(''); setBulkSelected(new Set()); }}
                data-testid="receipt-recent-last-btn">
                Last 25
              </Button>
              <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-[#52525B]"
                onClick={() => { setFilterDate(new Date().toISOString().split('T')[0]); setBulkSelected(new Set()); }}
                data-testid="receipt-recent-today-btn">
                Today
              </Button>
              {bulkSelected.size > 0 && canDelete && (
                <div className="flex items-center gap-2" data-testid="receipt-bulk-bar">
                  <span className="text-xs text-[#991B1B] font-semibold">{bulkSelected.size} selected</span>
                  <Button variant="ghost" size="sm" onClick={() => setBulkSelected(new Set())} className="text-xs h-7" data-testid="bulk-clear-receipts">
                    Clear
                  </Button>
                  <Button size="sm" onClick={() => setConfirmBulk(true)} className="bg-[#EF4444] text-white hover:bg-[#DC2626] hover:text-white rounded-lg text-xs h-7" data-testid="bulk-delete-receipts-btn">
                    <Trash size={12} className="mr-1" /> Delete Selected
                  </Button>
                </div>
              )}
            </div>
          </div>
          {(() => {
            // Filter the recent receipt list down to the picked day.
            // Falls back to created_at when r.date is missing.
            const displayed = recentEntries(recentReceipts, filterDate);
            if (displayed.length === 0) {
              return <p className="text-sm text-[#A1A1AA] italic text-center py-6" data-testid="receipt-recent-empty">{filterDate ? `No receipts on ${new Date(filterDate).toLocaleDateString('en-IN')}` : 'No receipts yet'}</p>;
            }
            return (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  {canDelete && (
                    <TableHead className="w-8 px-2">
                      {(() => {
                        const allTicked = displayed.length > 0 && displayed.every(r => bulkSelected.has(r.voucher_number));
                        return (
                          <input
                            type="checkbox"
                            checked={allTicked}
                            onChange={e => {
                              if (e.target.checked) setBulkSelected(new Set(displayed.map(r => r.voucher_number)));
                              else setBulkSelected(new Set());
                            }}
                            className="h-3.5 w-3.5 rounded border-[#3B82F6] accent-[#3B82F6] cursor-pointer"
                            title={allTicked ? 'Clear selection' : 'Select all'}
                            data-testid="bulk-select-all-receipts"
                          />
                        );
                      })()}
                    </TableHead>
                  )}
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Date</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Voucher</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Customer</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Linked Bill</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Account</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Amount</TableHead>
                  {canEdit && <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-center w-20">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayed.map(r => (
                  <Fragment key={r.voucher_number}>
                  <TableRow className={`border-[#E4E4E7] ${editingVoucher?.voucher_number === r.voucher_number ? 'bg-[#EFF6FF]' : (bulkSelected.has(r.voucher_number) ? 'bg-[#FEF2F2]' : '')}`} data-testid={`receipt-row-${r.voucher_number}`}>
                    {canDelete && (
                      <TableCell className="w-8 px-2">
                        <input
                          type="checkbox"
                          checked={bulkSelected.has(r.voucher_number)}
                          onChange={e => {
                            setBulkSelected(prev => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(r.voucher_number); else next.delete(r.voucher_number);
                              return next;
                            });
                          }}
                          className="h-3.5 w-3.5 rounded border-[#3B82F6] accent-[#3B82F6] cursor-pointer"
                          title="Select for bulk delete"
                          data-testid={`bulk-select-receipt-${r.voucher_number}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="text-xs text-[#52525B]">{r.date ? new Date(r.date).toLocaleDateString('en-IN') : '—'}</TableCell>
                    <TableCell>
                      <Badge className="rounded-md text-[10px] bg-[#DCFCE7] text-[#15803D]" variant="outline">
                        {r.voucher_number}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs"><User size={11} className="inline mr-1" />{r.customer_name || '—'}</TableCell>
                    <TableCell className="text-xs text-[#52525B]">
                      {r.invoice_number ? (
                        <span className="inline-flex items-center gap-1"><FileText size={11} />{r.invoice_number}</span>
                      ) : <span className="text-[#A1A1AA] italic">—</span>}
                    </TableCell>
                    <TableCell className="text-xs text-[#52525B]">{r.account_name || '—'}</TableCell>
                    <TableCell className="text-xs text-right font-mono font-semibold text-[#10B981]">{fmt(r.amount)}</TableCell>
                    {canEdit && (
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => startEdit(r)}
                            className="text-[#3B82F6] hover:bg-[#EFF6FF] rounded p-1"
                            title="Edit receipt"
                            data-testid={`edit-receipt-${r.voucher_number}`}>
                            <PencilSimple size={14} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(r)}
                            className="text-[#EF4444] hover:bg-[#FEE2E2] rounded p-1"
                            title="Delete receipt"
                            data-testid={`delete-receipt-${r.voucher_number}`}>
                            <Trash size={14} />
                          </button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                  {/* Apply-advance sub-vouchers rendered just below the parent
                      receipt. Each row shows the sub-voucher (e.g. RCP-0010/A1),
                      the amount applied and the target invoice number — in
                      small text so the register's primary cash flow stays
                      uncluttered. */}
                  {(r.applied_against || []).map(adj => (
                    <TableRow key={adj.sub_voucher} className={`border-[#E4E4E7] ${editingVoucher?.voucher_number === r.voucher_number ? 'bg-[#EFF6FF]' : ''}`} data-testid={`adj-mini-${adj.sub_voucher}`}>
                      {canDelete && <TableCell className="w-8 px-2" />}
                      <TableCell className="text-[10px] text-[#A1A1AA] pl-6">
                        {adj.date ? new Date(adj.date).toLocaleDateString('en-IN') : ''}
                      </TableCell>
                      <TableCell className="text-[10px] text-[#52525B] italic" colSpan={4 + (canEdit ? 0 : 0)}>
                        <span className="text-[#A1A1AA] mr-1">&#8627;</span>
                        <span className="font-mono text-[#64748B] mr-1.5">{adj.sub_voucher}</span>
                        {fmt(adj.amount)} applied to
                        {adj.invoice_number ? <span className="font-mono text-[#3B82F6] ml-1">{adj.invoice_number}</span> : <span className="ml-1">—</span>}
                      </TableCell>
                      <TableCell className="text-[10px] text-[#A1A1AA] text-right font-mono">{/* no totals impact */}</TableCell>
                      {canEdit && <TableCell />}
                    </TableRow>
                  ))}
                  {(r.mode_changes || []).map((mc, i) => (
                    <TableRow key={`mc-${i}`} className="border-[#E4E4E7] bg-[#FFFBEB]/60" data-testid={`mode-change-${r.voucher_number}-${i}`}>
                      {canDelete && <TableCell className="w-8 px-2" />}
                      <TableCell className="text-[10px] text-[#A1A1AA] pl-6">{mc.changed_at ? new Date(mc.changed_at).toLocaleDateString('en-IN') : ''}</TableCell>
                      <TableCell className="text-[10px] text-[#92400E]" colSpan={5 + (canEdit ? 1 : 0)}>
                        <ArrowsLeftRight size={11} className="inline mr-1" />
                        Mode changed <span className="font-semibold">{mc.from_name}</span> → <span className="font-semibold">{mc.to_name}</span>
                        {' '}by <span className="font-semibold">{mc.changed_by_name || '—'}</span>
                        {' '}on {mc.changed_at ? new Date(mc.changed_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
            );
          })()}
        </CardContent>
      </Card>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-xl border-[#E4E4E7]" data-testid="delete-receipt-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete receipt {deleteTarget?.voucher_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              This reverses {fmt(deleteTarget?.amount || 0)} from <span className="font-semibold">{deleteTarget?.account_name}</span>
              {deleteTarget?.kind === 'invoice' && deleteTarget?.invoice_number && (
                <> and re-opens the balance on <span className="font-semibold">{deleteTarget.invoice_number}</span></>
              )}
              {deleteTarget?.kind === 'advance' && (
                <> and reduces <span className="font-semibold">{deleteTarget.customer_name}</span>'s held advance</>
              )}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" data-testid="delete-receipt-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteReceipt} className="bg-[#EF4444] text-white hover:bg-[#DC2626] rounded-xl" data-testid="delete-receipt-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirmation. */}
      <AlertDialog open={confirmBulk} onOpenChange={(v) => { if (!v && !bulkDeleting) setConfirmBulk(false); }}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {bulkSelected.size} receipt{bulkSelected.size === 1 ? '' : 's'}?</AlertDialogTitle>
            <AlertDialogDescription>
              Each receipt's ledger entries will be reversed and any linked invoice balances reopened. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting} className="rounded-lg" data-testid="bulk-delete-receipts-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={bulkDeleting}
              onClick={performBulkDelete}
              className="bg-[#EF4444] text-white hover:bg-[#DC2626] rounded-lg disabled:opacity-50"
              data-testid="bulk-delete-receipts-confirm"
            >
              {bulkDeleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
